import { useConcatPages } from "../../../../shared/infrastructure";
import { useFetch } from "../../../../public/auth/infrastructure/store/fetch-context";
import { useOffers } from "../store/offers-context";
import {
  BlockOfferPayload,
  CustomOrderT,
  NegociationT,
  OfferT,
  UseCustomOffersFilters,
  UseGetOffersFilters,
} from "../../domain";
import {
  PageAttr,
  StatusErrorResponse,
} from "../../../../shared/domain/entities/fetch-entities";
import {
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

const offerKeys = {
  all: ["offers"] as const,
  bothList: () => [...offerKeys.all, "both"] as const,
  both: (filters: UseGetOffersFilters) =>
    [...offerKeys.bothList(), filters] as const,
  queueList: () => [...offerKeys.bothList(), "queue"] as const,
  queue: (filters: UseGetOffersFilters) =>
    [...offerKeys.queueList(), filters] as const,
  historyList: () => [...offerKeys.bothList(), "history"] as const,
  history: (filters: UseGetOffersFilters) =>
    [...offerKeys.historyList(), filters] as const,
  offerLogs: (section: "history" | "orders", filters: UseGetOffersFilters) =>
    [...offerKeys.all, section, "logs", filters] as const,
  customOffer: (filters: UseCustomOffersFilters) =>
    [...offerKeys.all, "custom", filters] as const,
  lowest: (approvalId: OfferT["approval_id"]) =>
    [...offerKeys.all, approvalId] as const,
};

const OFFERS_ENDPOINT = "approvals";

const useInfiniteGetOffers = (
  section: "queue" | "history" | "all",
  filters: UseGetOffersFilters = {},
  config: UseInfiniteQueryOptions<PageAttr<OfferT>> = {}
): UseInfiniteQueryResult<PageAttr<OfferT>> & {
  allPages: PageAttr<OfferT>["data"];
} => {
  const fetch = useFetch();

  const response = useInfiniteQuery<PageAttr<OfferT>>(
    offerKeys[section === "all" ? "both" : section](filters),
    async ({ pageParam: page = 1 }: { pageParam?: number }) => {
      const response = await fetch.get<PageAttr<OfferT>>(
        `${OFFERS_ENDPOINT}/${section}`,
        {
          params: {
            page,
            sort_by: "created_at",
            order_by: "DESC",
            limit: 5,
            ...filters,
          },
        }
      );

      return response.data;
    },
    {
      ...config,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.data.length > 0 ? allPages.length + 1 : undefined,
    }
  );

  const allPages = useConcatPages(response.data?.pages ?? []);

  return {
    ...response,
    allPages,
  };
};

const useInfiniteGetOfferLogs = (
  section: "history" | "orders",
  filters: UseGetOffersFilters = {},
  config: UseInfiniteQueryOptions<PageAttr<OfferT>> = {}
): UseInfiniteQueryResult<PageAttr<OfferT>> & {
  allPages: PageAttr<OfferT>["data"];
} => {
  const fetch = useFetch();

  const response = useInfiniteQuery<PageAttr<OfferT>>(
    offerKeys.offerLogs(section, filters),
    async ({ pageParam: page = 1 }: { pageParam?: number }) => {
      const response = await fetch.get<PageAttr<OfferT>>(
        `${section === "history" ? OFFERS_ENDPOINT : ""}/${section}/logs`,
        {
          params: {
            page,
            ...filters,
          },
        }
      );

      return response.data;
    },
    {
      ...config,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.data.length > 0 ? allPages.length + 1 : undefined,
    }
  );

  const allPages = useConcatPages(response.data?.pages ?? []);

  return {
    ...response,
    allPages,
  };
};

type NegociationResponse = {
  data: Array<NegociationT>;
};

const useOfferNegociations = (
  approval_id: number
): UseQueryResult<NegociationResponse> => {
  const fetch = useFetch();

  return useQuery<NegociationResponse>([], async () => {
    const response = await fetch.get<NegociationResponse>(
      `approvals/${approval_id}/negotiations`
    );

    return response.data;
  });
};

type CustomOfferResponse = {
  data: CustomOrderT;
};

const useCustomOffer = (
  filters: UseCustomOffersFilters
): UseQueryResult<CustomOfferResponse> => {
  const fetch = useFetch();

  return useQuery<CustomOfferResponse>(
    offerKeys.customOffer(filters),
    async () => {
      const response = await fetch.get<CustomOfferResponse>(
        `${OFFERS_ENDPOINT}/custom-data`,
        { params: filters }
      );

      return response.data;
    }
  );
};

type UpdateOfferStatusPayload = {
  approval_id: number;
  status: "approved" | "rejected" | "counter";
  counter_offer?: number;
  status_comment?: string;
};

type UpdateOfferStatusResponse = {
  data: OfferT;
};

const useUpdateOfferStatus = (): UseMutationResult<
  UpdateOfferStatusResponse,
  StatusErrorResponse,
  UpdateOfferStatusPayload
> => {
  const fetch = useFetch();
  const queryClient = useQueryClient();
  const { updateNewOffers } = useOffers();

  return useMutation<
    UpdateOfferStatusResponse,
    StatusErrorResponse,
    UpdateOfferStatusPayload
  >(
    async (payload) =>
      (
        await fetch.put<UpdateOfferStatusResponse>(
          `${OFFERS_ENDPOINT}/queue/${payload.approval_id}/status`,
          payload
        )
      ).data,
    {
      onSuccess: async () => {
        updateNewOffers();
        await queryClient.invalidateQueries(offerKeys.bothList());
      },
    }
  );
};

type CreateOfferPayload =
  | {
      type: "dedicated";
      payload: {
        carrier_id: string;
        comments?: string;
        destination_id: number;
        equipment_type_id: string;
        origin_id: number;
        rate: number;
        radius?: number;
        distance_from?: string;
      };
    }
  | {
      type: "single";
      payload: {
        carrier_id: string;
        comments?: string;
        commission: number;
        movement_number: string;
        rate: number;
        truck_location_id: number;
        is_empty_truck: boolean;
      };
    };

type CreateOfferResponse = {
  data: OfferT;
};

const useCreateOffer = (): UseMutationResult<
  CreateOfferResponse,
  StatusErrorResponse,
  CreateOfferPayload
> => {
  const fetch = useFetch();
  const queryClient = useQueryClient();
  const { updateNewOffers } = useOffers();

  return useMutation<
    CreateOfferResponse,
    StatusErrorResponse,
    CreateOfferPayload
  >(
    async (params) =>
      (
        await fetch.post<CreateOfferResponse>(
          `${OFFERS_ENDPOINT}/${params.type}`,
          params.payload
        )
      ).data,
    {
      onSuccess: async () => {
        updateNewOffers();
        await queryClient.invalidateQueries(offerKeys.queueList());
      },
    }
  );
};

type UpdateOfferPayload =
  | {
      type: "dedicated";
      offerId: number;
      payload: Partial<{
        carrier_id: string;
        comments: string;
        destination_id: number;
        equipment_type_id: string;
        origin_id: number;
        rate: number;
        radius: number;
        distance_from: string;
        truck_location_id: number;
        is_empty_truck: boolean;
      }>;
    }
  | {
      type: "single";
      offerId: number;
      payload: Partial<{
        carrier_id: string;
        comments: string;
        commission: number;
        movement_number: string;
        rate: number;
      }>;
    };

const useUpdateOffer = (): UseMutationResult<
  CreateOfferResponse,
  StatusErrorResponse,
  UpdateOfferPayload
> => {
  const fetch = useFetch();
  const queryClient = useQueryClient();
  const { updateNewOffers } = useOffers();

  return useMutation<
    CreateOfferResponse,
    StatusErrorResponse,
    UpdateOfferPayload
  >(
    async (params) =>
      (
        await fetch.patch<CreateOfferResponse>(
          `${OFFERS_ENDPOINT}/queue/${params.offerId}`,
          params.payload
        )
      ).data,
    {
      onSuccess: async () => {
        updateNewOffers();
        await queryClient.invalidateQueries(offerKeys.queueList());
      },
    }
  );
};

type DeleteOfferPayload = {
  offerId: number;
};

const useDeleteOffer = (): UseMutationResult<
  CreateOfferResponse,
  StatusErrorResponse,
  DeleteOfferPayload
> => {
  const fetch = useFetch();
  const queryClient = useQueryClient();
  const { updateNewOffers } = useOffers();

  return useMutation<
    CreateOfferResponse,
    StatusErrorResponse,
    DeleteOfferPayload
  >(
    async (params) =>
      (
        await fetch.delete<CreateOfferResponse>(
          `${OFFERS_ENDPOINT}/queue/${params.offerId}`
        )
      ).data,
    {
      onSuccess: async () => {
        updateNewOffers();
        await queryClient.invalidateQueries(offerKeys.queueList());
      },
    }
  );
};

type RenewOfferPayload = {
  offerId: number;
  payload: {
    rate: number;
  };
};

const useRenewOffer = (): UseMutationResult<
  CreateOfferResponse,
  StatusErrorResponse,
  RenewOfferPayload
> => {
  const fetch = useFetch();
  const queryClient = useQueryClient();
  const { updateNewOffers } = useOffers();

  return useMutation<
    CreateOfferResponse,
    StatusErrorResponse,
    RenewOfferPayload
  >(
    async (params) =>
      (
        await fetch.post<CreateOfferResponse>(
          `${OFFERS_ENDPOINT}/${params.offerId}/renew`,
          params.payload
        )
      ).data,
    {
      onSuccess: async () => {
        updateNewOffers();
        await queryClient.invalidateQueries(offerKeys.all);
      },
    }
  );
};

type UseLowerRateOfferResponse = {
  data: {
    is_lowest_rate: boolean;
  };
};

const useLowerRateOffer = (
  approvalId: OfferT["approval_id"]
): UseQueryResult<UseLowerRateOfferResponse> => {
  const fetch = useFetch();

  return useQuery<UseLowerRateOfferResponse>(
    offerKeys.lowest(approvalId),
    async () => {
      const response = await fetch.get<UseLowerRateOfferResponse>(
        `${OFFERS_ENDPOINT}/${approvalId}/has-lowest-rate`
      );

      return response.data;
    }
  );
};

const useBlockOffer = (): UseMutationResult<
  unknown,
  StatusErrorResponse,
  BlockOfferPayload
> => {
  const fetch = useFetch();
  const { updateNewOffers } = useOffers();

  return useMutation<unknown, StatusErrorResponse, BlockOfferPayload>(
    async ({ approvalId }) =>
      (await fetch.post<unknown>(`${OFFERS_ENDPOINT}/${approvalId}/block`))
        .data,
    {
      onSuccess: () => {
        updateNewOffers();
      },
    }
  );
};

const useUnblockOffer = (): UseMutationResult<
  unknown,
  StatusErrorResponse,
  BlockOfferPayload
> => {
  const fetch = useFetch();
  const { updateNewOffers } = useOffers();

  return useMutation<unknown, StatusErrorResponse, BlockOfferPayload>(
    async ({ approvalId }) =>
      (
        await fetch.post<UpdateOfferStatusResponse>(
          `${OFFERS_ENDPOINT}/${approvalId}/unblock`
        )
      ).data,
    {
      onSuccess: () => {
        updateNewOffers();
      },
    }
  );
};

export {
  offerKeys,
  useCustomOffer,
  useInfiniteGetOffers,
  useInfiniteGetOfferLogs,
  useUpdateOfferStatus,
  useCreateOffer,
  useUpdateOffer,
  useDeleteOffer,
  useRenewOffer,
  useLowerRateOffer,
  useBlockOffer,
  useUnblockOffer,
  useOfferNegociations,
};
